// lib
import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

// components
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'
import HeadHelp from '@aytm/app/stats/page/question-views/cjc/components/head/HeadHelp'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/question-views/cjc/components/head/styles/Head.module.scss'
import { NO_PRINT } from '@aytm/app/stats/page/question-views/components/lib/constants'

// The right-hand side of both conjoint heads: the view-switch CTAs, the help popup and the export
// dropdown. Which CTA shows follows the view the block is in; the export options come from the
// question type's own head.
const HeadOptions = ({
  questionType,
  exportOptions,
  isLite,
  isSummaryChartView,
  isAwaitingFullSlice,
  showExport,
  fullViewOnly,
  onExport,
  onShowBar,
  onShowVisual
}) => {
  const showBar = !fullViewOnly && !isLite && !isSummaryChartView
  const showVisual = !fullViewOnly && (isLite || isSummaryChartView)
  const helpMode = fullViewOnly ? null : isLite ? 'lite' : isSummaryChartView ? 'chart' : 'full'

  // Which CTA is mid-switch - guards against double clicks. Reset once the switch has landed:
  // the view has changed AND, for the lite -> full switch, the full data has arrived. The view
  // flips first and draws the other CTA in the same spot, so clearing the mark on the flip alone
  // let the second click of a double click act on it (master kept the link busy until the redraw).
  const [busyCta, setBusyCta] = useState(null)

  const onBarClick = e => {
    e.preventDefault()
    if (busyCta) return
    setBusyCta('bar')
    onShowBar()
  }

  const onVisualClick = e => {
    e.preventDefault()
    if (busyCta) return
    setBusyCta('visual')
    onShowVisual()
  }

  const onExportClick = (key, callback) => {
    const option = exportOptions.find(o => o.key === key)
    if (!option) {
      callback && callback()
      return
    }
    onExport({ fileType: option.fileType, exportType: option.exportType, exportMethod: option.exportMethod }, callback)
  }

  useEffect(() => {
    if (!isAwaitingFullSlice) setBusyCta(null)
  }, [isLite, isSummaryChartView, isAwaitingFullSlice])

  return (
    <div className={`${style.options} ${NO_PRINT}`}>
      {showBar ? (
        <span
          className={classNames(style.view, style['show-bar'], { [style.loading]: 'bar' === busyCta })}
          title="Switch to attribute view"
          {...pressable(onBarClick)}
        >
          <i className="icon-aytmfd-bars-chart-v2" />
          <span className={style['show-bar-text']}>
            Attribute
            <br />
            View
          </span>
        </span>
      ) : null}

      {showVisual ? (
        <span
          className={classNames(style.view, style['show-visual'], { [style.loading]: 'visual' === busyCta })}
          title="Switch to simulator view"
          {...pressable(onVisualClick)}
        >
          <i className="icon-aytmfd-stacked-bars-chart-v2" />
          <span className={style['show-visual-text']}>
            Simulator
            <br />
            View
          </span>
        </span>
      ) : null}

      {helpMode ? (
        <Popup
          width="360px"
          trigger="click"
          direction="lefttop"
          popupOffset={9}
          hideOtherPopupsOnShow
          content={() => <HeadHelp questionType={questionType} mode={helpMode} />}
        >
          <i className="icon-aytmfd-help-v2" />
        </Popup>
      ) : null}

      {showExport ? <ExportDropdown options={exportOptions} onExport={onExportClick} /> : null}
    </div>
  )
}

HeadOptions.propTypes = {
  questionType: PropTypes.oneOf(['cjc', 'cjd']),
  exportOptions: PropTypes.array,
  isLite: PropTypes.bool,
  isSummaryChartView: PropTypes.bool,
  isAwaitingFullSlice: PropTypes.bool,
  showExport: PropTypes.bool,
  fullViewOnly: PropTypes.bool,
  onExport: PropTypes.func,
  onShowBar: PropTypes.func,
  onShowVisual: PropTypes.func
}

HeadOptions.displayName = 'ConjointHeadOptions'

export default HeadOptions
